import React, { useCallback, useMemo } from "react";

import { sortBy } from "lodash";
import { useDispatch } from "react-redux";

import Alert from "@busbud/design-system-components/dist/Alert";
import Button from "@busbud/design-system-components/dist/Button";
import Dialog from "@busbud/design-system-components/dist/Dialog/Dialog";
import DialogContent from "@busbud/design-system-components/dist/DialogContent/DialogContent";
import DialogTitle from "@busbud/design-system-components/dist/DialogTitle/DialogTitle";
import Divider from "@busbud/design-system-components/dist/Divider";
import LongArrowDownMD from "@busbud/design-system-components/dist/Icons/LongArrowDown/MD";
import Label from "@busbud/design-system-components/dist/Label/Label";
import Stack from "@busbud/design-system-components/dist/Stack";

import { AppleWalletTicket } from "@app/components/app-wallet-ticket";
import { GooglePayTicketImage } from "@app/components/app-wallet-ticket/google-pay-ticket";
import { useAppContext } from "@app/helpers/hooks";
import { getPurchaseTicketBundle } from "@app/helpers/purchase/get-purchase-ticket";
import { getPurchaseTicketsPerLeg } from "@app/helpers/purchase/get-purchase-tickets-per-leg";
import { clickedDownloadTickets } from "@app/tracking/account-tracking";
import type { NapiV2Purchase } from "@app/types/napi-v2-purchase-types";
import type { NapiV2TicketItem } from "@app/types/napi-v2-tickets-types";

import { PassengerTicketLabel } from "./components/passenger-ticket";
import { useStyles } from "./download-tickets-with-bundle.styles";
import { closeModal } from "../../../store/state/modals";

interface Props {
  purchase: NapiV2Purchase;
}

export const DownloadTicketsWithBundle: React.FC<Props> = ({ purchase }) => {
  const { translator, tracker } = useAppContext();
  const dispatch = useDispatch();
  const { classes } = useStyles();
  const { sorted_outbound_tickets, sorted_return_tickets } = useMemo(() => {
    const { return_tickets, outbound_tickets } =
      getPurchaseTicketsPerLeg(purchase);
    const sorted_outbound_tickets = sortBy(outbound_tickets, [
      "passenger.first_name",
      "passenger.last_name"
    ]);
    const sorted_return_tickets = sortBy(return_tickets || [], [
      "passenger.first_name",
      "passenger.last_name"
    ]);
    return { sorted_outbound_tickets, sorted_return_tickets };
  }, [purchase]);

  const outbound_tickets_sent_by_supplier = useMemo(() => {
    return sorted_outbound_tickets.every(
      ticket => ticket.ticket_type === "supplier-email"
    );
  }, [sorted_outbound_tickets]);

  const return_tickets_sent_by_supplier = useMemo(() => {
    return sorted_return_tickets.every(
      ticket => ticket.ticket_type === "supplier-email"
    );
  }, [sorted_return_tickets]);

  const ticket_bundle = getPurchaseTicketBundle(purchase);
  const onClose = useCallback(() => dispatch(closeModal()), [dispatch]);

  return (
    <Dialog open maxWidth="sm" fullWidth onClose={onClose}>
      <DialogTitle onClose={onClose}>
        <Label size="lg" fontWeight="bold">
          {translator.t("!download-tickets.title")}
        </Label>
      </DialogTitle>
      <Divider />

      <DialogContent className={classes.content}>
        <Stack direction="column" spacing={3}>
          {!!ticket_bundle && (
            <Stack
              justifyContent="center"
              spacing={2}
              alignItems="center"
              direction={{ xs: "column", md: "row" }}
            >
              {!!ticket_bundle.pkpasses_url && (
                <AppleWalletTicket
                  pkpass_url={ticket_bundle.pkpasses_url}
                  className={classes.addToWalletButton}
                  tracker_name="apple_wallet_bundle"
                />
              )}
              {!!ticket_bundle.transit_object_jwt && (
                <GooglePayTicketImage
                  transit_object_jwt={ticket_bundle.transit_object_jwt}
                  className={classes.addToWalletButton}
                  tracker_name="google_wallet_bundle"
                />
              )}
            </Stack>
          )}

          {!!ticket_bundle?.pdf_url && (
            <Button
              size="md"
              variant="contained"
              endIcon={<LongArrowDownMD />}
              color="primary"
              fullWidth={true}
              href={ticket_bundle.pdf_url}
              // @ts-expect-error
              target="_blank"
              onClick={() =>
                tracker.track(clickedDownloadTickets("pdf_bundle"))
              }
            >
              {translator.t(
                "!purchase-confirmation.ticket-button.download-all"
              )}
            </Button>
          )}

          {!!sorted_return_tickets?.length && (
            <Label size="xs" color="text.tertiary.opaque" fontWeight="bold">
              {translator.t("!departure.type.outbound")}
            </Label>
          )}
          {!!outbound_tickets_sent_by_supplier ? (
            <Alert severity="informative">
              {translator.t("!download-tickets.supplier-email-alert")}
            </Alert>
          ) : (
            sorted_outbound_tickets.map(ticket => (
              <PassengerLabelAndButton ticket={ticket} key={ticket.id} />
            ))
          )}
          {!!sorted_return_tickets?.length && (
            <>
              <Divider />
              <Label size="xs" color="text.tertiary.opaque" fontWeight="bold">
                {translator.t("!departure.type.return")}
              </Label>
              {!!return_tickets_sent_by_supplier ? (
                <Alert severity="informative">
                  {translator.t("!download-tickets.supplier-email-alert")}
                </Alert>
              ) : (
                sorted_return_tickets.map(ticket => (
                  <PassengerLabelAndButton ticket={ticket} key={ticket.id} />
                ))
              )}
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

const PassengerLabelAndButton: React.FC<{ ticket: NapiV2TicketItem }> = ({
  ticket
}) => {
  const { translator, tracker } = useAppContext();
  return (
    <Stack
      key={ticket.id}
      direction={{ xs: "column", md: "row" }}
      justifyContent="space-between"
      alignItems={{ xs: "flex-start", md: "center" }}
      spacing={1.5}
    >
      <PassengerTicketLabel ticket={ticket} />
      {!!ticket.urls.pdf && (
        <Button
          size="md"
          variant="contained"
          endIcon={<LongArrowDownMD />}
          color="tertiary"
          fullWidth={{ xs: true, md: false }}
          href={ticket.urls.pdf}
          // @ts-expect-error
          target="_blank"
          onClick={() => tracker.track(clickedDownloadTickets("pdf"))}
        >
          {translator.t("!purchase-confirmation.ticket-button.download")}
        </Button>
      )}
    </Stack>
  );
};
